/**
 * @file Category-only operator wording for a UC.010 registration IHI search resubmission result.
 * @module pages/admin/hi-alerts/registrationAmendedSearchOutcome
 *
 * CU-14ygtkj8z3m (HI_010_005818). Maps the backend's bounded `outcome` and
 * `assignment` labels to fixed wording. It never receives or shows an IHI, a
 * Search Type token, a match count or a request/response body, never implies an
 * assignment the backend did not report, and falls back safely for unknown labels.
 * Deliberately separate from the UC.015 wording: `assignment` is not `assignment_outcome`.
 */

export const REGISTRATION_OUTCOME_SINGLE_MATCH = "single_match";
export const REGISTRATION_OUTCOME_NO_MATCH = "no_match";
export const REGISTRATION_OUTCOME_MULTIPLE_MATCH = "multiple_match";
export const REGISTRATION_OUTCOME_INDETERMINATE = "indeterminate";

export const REGISTRATION_ASSIGNMENT_ASSIGNED = "assigned";
export const REGISTRATION_ASSIGNMENT_NOT_ASSIGNED = "not_assigned";
export const REGISTRATION_ASSIGNMENT_FAILED = "failed";

/** The backend leaves the originating review open after every outcome; only explicit operator resolution closes it. */
export const REGISTRATION_REVIEW_REMAINS_OPEN_NOTE =
	"The original registration no-match review remains open for explicit operator resolution.";

export interface RegistrationAmendedSearchOutcomeMessage {
	severity: "success" | "info" | "warning";
	title: string;
	detail: string;
}

export const describeRegistrationAmendedSearchOutcome = (
	outcome: string | null | undefined,
	assignment: string | null | undefined
): RegistrationAmendedSearchOutcomeMessage => {
	switch (outcome) {
		case REGISTRATION_OUTCOME_NO_MATCH:
			return {
				severity: "info",
				title: "No match found",
				detail:
					"The amended registration search returned no match. No IHI was assigned and no further search was attempted.",
			};
		case REGISTRATION_OUTCOME_MULTIPLE_MATCH:
			return {
				severity: "warning",
				title: "Multiple matches found",
				detail:
					"More than one possible match was returned, so no single record could be selected. No IHI was assigned.",
			};
		case REGISTRATION_OUTCOME_INDETERMINATE:
			return {
				severity: "warning",
				title: "Result could not be determined",
				detail: "The amended registration search result could not be safely determined. No IHI was assigned.",
			};
		case REGISTRATION_OUTCOME_SINGLE_MATCH:
			return describeSingleMatch(assignment);
		default:
			return {
				severity: "info",
				title: "Amended registration search submitted",
				detail: "The amended registration search was submitted. No IHI was assigned automatically.",
			};
	}
};

const describeSingleMatch = (assignment: string | null | undefined): RegistrationAmendedSearchOutcomeMessage => {
	switch (assignment) {
		case REGISTRATION_ASSIGNMENT_ASSIGNED:
			return {
				severity: "success",
				title: "Single match found — IHI assigned",
				detail:
					"A single matching record was found and its IHI was assigned to the patient record. The IHI itself is not shown here. The original review remains open until it is explicitly resolved.",
			};
		case REGISTRATION_ASSIGNMENT_NOT_ASSIGNED:
			return {
				severity: "warning",
				title: "Single match found — no IHI assigned",
				detail: "A single matching record was returned, but no IHI was assigned to the patient record.",
			};
		case REGISTRATION_ASSIGNMENT_FAILED:
			return {
				severity: "warning",
				title: "Single match found — assignment not completed",
				detail:
					"A single matching record was returned, but the assignment could not be completed. Check the review before taking further action.",
			};
		default:
			return {
				severity: "warning",
				title: "Single match found",
				detail: "A single matching record was returned. No assignment is reported for this result.",
			};
	}
};
